import { Controller } from "stimulus";
import { Chart } from "chart.js";
import { changeDpiDataUrl } from "changedpi";
import * as htmlToImage from "html-to-image";
import moment from "moment";

// Render downloads at a fixed multiple of CSS size regardless of the user's
// screen, so a standard (1x) monitor gets the same crisp PNG as a Retina one.
// 3x keeps small chart text sharp when the image is enlarged in PowerPoint or
// printed, and is well under browser canvas size limits for our chart sizes.
export const DEFAULT_PIXEL_RATIO = 3;

// PowerPoint sizes a PNG without DPI metadata as if it were 96 DPI (a customer
// deck showed our 580px-wide download inserted at 6.04in), and honors DPI
// metadata when present. So we tag the file with 96 * pixelRatio DPI (288 at
// 3x): it inserts at the same physical size as a 1x download, just sharper,
// instead of 3x larger than the slide. (Keynote ignores DPI metadata and places
// images at 1pt per pixel, shrinking oversized ones to fit the slide.)
export const BASE_DPI = 96;

export default class extends Controller {
  static targets = ["exclude", "image", "frame", "footer"];

  static values = { htmlToImageKnownFaulty: Boolean, pixelRatio: Number };

  get pixelRatio() {
    return this.hasPixelRatioValue ? this.pixelRatioValue : DEFAULT_PIXEL_RATIO;
  }

  async download() {
    const monotonicTimestamp = moment().format("YYYY-MM-DD_kk-mm-ss");
    const filename = `${monotonicTimestamp}-chart.png`;
    const width = this.element.offsetWidth;
    const height = this.element.offsetHeight + this.footerTarget.offsetHeight;
    const { pixelRatio } = this;

    const options = {
      backgroundColor: "#fff",
      filter: el => !Array.from(this.excludeTargets).includes(el),
      pixelRatio,
    };

    this.frameTarget.setAttribute("style", `width:${width}px; height:${height}px`);

    const imageData = await this.withChartsRenderedAt(pixelRatio, () =>
      this.toPng(this.element, options),
    );

    this.insertImage(imageData, width);

    const data = await this.toPng(this.frameTarget, options);

    this.saveFile(changeDpiDataUrl(data, BASE_DPI * pixelRatio), filename);
  }

  // Chart.js canvases hold a bitmap sized to window.devicePixelRatio, so on a
  // 1x monitor html-to-image would just upscale a blurry 1x bitmap. Temporarily
  // re-render them at the export ratio (the on-screen CSS size is unchanged).
  async withChartsRenderedAt(pixelRatio, callback) {
    const charts = Array.from(this.element.querySelectorAll("canvas"))
      .map(canvas => Chart.getChart(canvas))
      .filter(Boolean);
    const originalRatios = charts.map(chart => chart.options.devicePixelRatio);

    charts.forEach(chart => {
      chart.options.devicePixelRatio = pixelRatio;
      chart.resize();
    });

    try {
      return await callback();
    } finally {
      charts.forEach((chart, i) => {
        chart.options.devicePixelRatio = originalRatios[i];
        chart.resize();
      });
    }
  }

  async toPng(element, options) {
    let dataUrl = await htmlToImage.toPng(element, options);

    let previousLength = -1;

    let i = 0;

    // Safari and Firefix cause issues with our htmltoimage library since they appear to not fully wait on images to load
    // https://github.com/bubkoo/html-to-image/issues/361
    // Chrome is not known to exhibit this behavior, so we skip the mitigation for performance
    if (this.htmlToImageKnownFaultyValue) {
      while (previousLength !== dataUrl.length && i < 10) {
        previousLength = dataUrl.length;
        // eslint-disable-next-line no-await-in-loop
        dataUrl = await htmlToImage.toPng(element, options);
        i += 1;
      }
    }

    return dataUrl;
  }

  // The first-pass image has pixelRatio times more pixels than the chart's CSS
  // size. Pin its display width so the second pass captures it at CSS size
  // while keeping every high-res pixel.
  insertImage(imageData, width) {
    const img = document.createElement("img");

    img.src = imageData;
    img.style.width = `${width}px`;
    this.imageTarget.appendChild(img);
  }

  saveFile(imageData, filename) {
    const anchor = document.createElement("a");

    anchor.setAttribute("href", imageData);
    anchor.setAttribute("download", filename);
    anchor.click();
    anchor.remove();
    this.imageTarget.removeChild(this.imageTarget.firstChild);
  }
}
